// Einstellungen → Fragen der Woche (Phase 2.E): Themen mit Anzahl, Trend, Beispielen, Handbuch-Abdeckung und Vorschlag.
import { useCallback, useEffect, useState } from 'react';
import { api, type FragenWoche } from '../lib/api.ts';
import { toast } from './Toast.tsx';

const ART: Record<string, string> = {
  handbuch: 'Handbuch-Kapitel',
  faq: 'FAQ auf der Website',
  zeitleiste: 'Satz in einer Zeitleisten-Mail',
  keiner: '–',
};

export function FragenDerWoche({ admin }: { admin: boolean }) {
  const [data, setData] = useState<{ laeuft: boolean; wochen: FragenWoche[] } | null>(null);
  const [sel, setSel] = useState(0);
  const load = useCallback(
    () =>
      api
        .fragenDerWoche()
        .then(setData)
        .catch((e: Error) => toast(e.message)),
    [],
  );
  useEffect(() => void load(), [load]);
  useEffect(() => {
    if (!data?.laeuft) return;
    const t = setInterval(() => void load(), 5000);
    return () => clearInterval(t);
  }, [data?.laeuft, load]);
  if (!data) return <div className="muted">wird geladen …</div>;
  const w = data.wochen[sel];
  const trend = (t: { anzahl: number; vorwoche: number | null }) =>
    t.vorwoche == null
      ? 'neu'
      : t.anzahl > t.vorwoche
        ? `▲ +${t.anzahl - t.vorwoche}`
        : t.anzahl < t.vorwoche
          ? `▼ ${t.anzahl - t.vorwoche}`
          : '= gleich';
  return (
    <div className="fw">
      <div className="fw-head">
        <select
          value={sel}
          onChange={(e) => setSel(Number(e.target.value))}
          disabled={!data.wochen.length}
        >
          {data.wochen.length === 0 && <option>noch keine Auswertung</option>}
          {data.wochen.map((x, i) => (
            <option key={x.woche} value={i}>
              {x.woche} · {x.mails} Mails, {x.mitFragen} mit Fragen
            </option>
          ))}
        </select>
        {admin && (
          <button
            className="btn sm"
            disabled={data.laeuft}
            onClick={() =>
              void api
                .fragenAuswerten()
                .then(() => {
                  toast('Bob wertet die letzten 7 Tage aus …');
                  void load();
                })
                .catch((e: Error) => toast(e.message))
            }
          >
            {data.laeuft ? 'Bob wertet aus …' : 'Jetzt auswerten (letzte 7 Tage)'}
          </button>
        )}
      </div>
      {w && w.themen.length === 0 && <div className="muted">Keine Fragen in dieser Woche.</div>}
      {w?.themen.map((t) => (
        <div key={t.thema} className="fw-thema">
          <div className="fw-t1">
            <b>{t.thema}</b>
            <span className="pill mut">{t.anzahl}×</span>
            <span className="muted" style={{ fontSize: 12 }}>
              {trend(t)}
            </span>
            <span
              className={`pill ${t.handbuch.deckt_ab ? 'ok' : 'warn'}`}
              title={t.handbuch.hinweis}
            >
              {t.handbuch.deckt_ab
                ? `Handbuch: ${t.handbuch.kapitel ?? 'ja'}`
                : 'nicht im Handbuch'}
            </span>
          </div>
          {t.handbuch.hinweis && (
            <div className="muted" style={{ fontSize: 12.5 }}>
              {t.handbuch.hinweis}
            </div>
          )}
          {t.vorschlag.art !== 'keiner' && (
            <div className="fw-v">
              <b>Vorschlag ({ART[t.vorschlag.art]}):</b> {t.vorschlag.text}
            </div>
          )}
          {t.beispiele.length > 0 && (
            <div className="muted" style={{ fontSize: 12 }}>
              Beispiele:{' '}
              {t.beispiele.map((b, i) => (
                <span key={b.threadId}>
                  {i > 0 && ' · '}
                  <a href={`/t/${b.threadId}`} target="_blank" rel="noreferrer">
                    {b.betreff || `Thread ${b.threadId}`}
                  </a>
                </span>
              ))}
            </div>
          )}
        </div>
      ))}
      {w && (
        <div className="muted" style={{ fontSize: 11.5 }}>
          Ausgewertet {new Date(w.erzeugtAm).toLocaleString('de-DE')} · {w.kostenEur.toFixed(2)} € ·
          nur Themen und Zahlen, keine personenbezogenen Daten. Wie oft Antworten nachgebessert
          wurden, kommt mit der Lernschleife (Phase 3).
        </div>
      )}
    </div>
  );
}
